import React, { useEffect, useRef, useState } from 'react';
import './CustomerAutocomplete.css';
const dateLabel = day => day ? day.split('-').reverse().join('.') : '';
const appointmentLabel = appointment => {
  if (!appointment) return '';
  const kind = appointment.kind === 'consultation' ? 'Beratung' : appointment.kind === 'retouch' ? 'Nachstechen' : 'Tattoo';
  return `${kind} · ${dateLabel(appointment.day)}${appointment.time ? `, ${appointment.time}` : ''}${appointment.artistName ? ` · ${appointment.artistName}` : ''}${appointment.locationName ? ` · ${appointment.locationName}` : ''}`;
};
export default function CustomerAutocomplete({ data, setData, locationId, isOpen }) {
  const [field, setField] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [active, setActive] = useState(-1);
  const [dismissed, setDismissed] = useState(false);
  const [position, setPosition] = useState({ above: false, height: 240 });
  const root = useRef(null);
  const inputRefs = useRef({});
  const requestVersion = useRef(0);
  const id = useRef(`customer-lookup-${Math.random().toString(36).slice(2)}`);
  const selected = data.autocompleteCustomer;
  const query = String(field === 'phone' ? data.phone || '' : data.clientName || '').trim();
  const eligible = field === 'phone' ? query.replace(/\D/g, '').length >= 4 : query.length >= 2;
  const open = isOpen && field && !selected && !dismissed && eligible;
  useEffect(() => {
    const version = ++requestVersion.current;
    setCustomers([]); setActive(-1); setError(''); setLoading(false);
    if (!open) return undefined;
    const controller = new AbortController();
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ q: query, field });
        if (locationId) params.set('locationId', String(locationId));
        const response = await fetch(`/api/customer-autocomplete?${params}`, { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw new Error('lookup');
        const result = await response.json();
        if (requestVersion.current === version) setCustomers(Array.isArray(result.customers) ? result.customers.slice(0, 5) : []);
      } catch (failure) {
        if (failure.name !== 'AbortError' && requestVersion.current === version) setError('Kundensuche gerade nicht verfügbar. Du kannst die Daten weiter manuell eingeben.');
      } finally { if (requestVersion.current === version) setLoading(false); }
    }, 250);
    return () => { clearTimeout(timer); controller.abort(); ++requestVersion.current; };
  }, [query, field, locationId, open]);
  useEffect(() => {
    if (!open) return undefined;
    const update = () => {
      const input = inputRefs.current[field];
      if (!input) return;
      const rect = input.getBoundingClientRect();
      const viewport = window.visualViewport;
      const top = viewport?.offsetTop || 0;
      const bottom = top + (viewport?.height || window.innerHeight);
      const below = Math.max(0, bottom - rect.bottom - 12);
      const above = Math.max(0, rect.top - top - 12);
      const useAbove = below < 160 && above > below;
      setPosition({ above: useAbove, height: Math.max(60, Math.min(290, useAbove ? above : below)) });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    window.visualViewport?.addEventListener('resize', update);
    window.visualViewport?.addEventListener('scroll', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      window.visualViewport?.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('scroll', update);
    };
  }, [open, field]);
  useEffect(() => {
    if (active >= 0) root.current?.querySelector(`[data-customer-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);
  const change = (key, value) => {
    ++requestVersion.current;
    setCustomers([]); setActive(-1); setDismissed(false);
    setData(previous => ({ ...previous, ...(key === 'clientName' && previous.autocompleteCustomer ? { phone: '', email: '', consultation_email: '' } : {}), [key]: value, autocompleteCustomer: null }));
  };
  const select = customer => {
    ++requestVersion.current;
    setData(previous => ({ ...previous, clientName: customer.clientName, phone: customer.phone || '', email: customer.email || '', consultation_email: customer.email || '', autocompleteCustomer: customer }));
    setCustomers([]); setDismissed(true); setField(null);
  };
  const keyboard = event => {
    if (!open || event.nativeEvent?.isComposing) return;
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); setDismissed(true); }
    if (event.key === 'ArrowDown' && customers.length) { event.preventDefault(); setActive(value => (value + 1) % customers.length); }
    if (event.key === 'ArrowUp' && customers.length) { event.preventDefault(); setActive(value => (value <= 0 ? customers.length : value) - 1); }
    if (event.key === 'Enter') { event.preventDefault(); if (active >= 0 && customers[active]) select(customers[active]); }
  };
  const suggestions = currentField => open && field === currentField && (
    <div className={`customer-suggestions ${position.above ? 'customer-suggestions-above' : ''}`} style={{ maxHeight: position.height }}>
      <div className="customer-suggestions-title" role="status">{loading ? 'Kunden werden gesucht …' : error || (customers.length ? 'Bestandskunden' : 'Kein Treffer · Daten manuell eingeben')}</div>
      <div role="listbox" id={`${id.current}-${currentField}`} aria-label="Bestandskunden">
        {customers.map((customer, index) => <div role="option" aria-selected={active === index} id={`${id.current}-${currentField}-${index}`} key={customer.key} data-customer-index={index}
          className={`customer-suggestion ${active === index ? 'is-active' : ''}`}
          onMouseDown={event => event.preventDefault()} onClick={() => select(customer)}>
          <div className="customer-suggestion-heading"><strong>{customer.clientName}</strong><span>{customer.locationName}</span></div>
          <div className="customer-suggestion-contact">{customer.phone || customer.email || 'Keine Kontaktdaten hinterlegt'}</div>
          {customer.lastAppointment && <div className="customer-suggestion-detail">Zuletzt: {appointmentLabel(customer.lastAppointment)}</div>}
          {customer.nextAppointment && <div className="customer-suggestion-next">Geplant: {appointmentLabel(customer.nextAppointment)}</div>}
        </div>)}
      </div>
      {!loading && !error && <button type="button" className="customer-manual" onMouseDown={event => event.preventDefault()} onClick={() => setDismissed(true)}>Ohne Auswahl fortfahren</button>}
    </div>
  );
  const inputProps = currentField => ({
    role: 'combobox', 'aria-autocomplete': 'list', 'aria-expanded': Boolean(open && field === currentField),
    'aria-controls': open && field === currentField ? `${id.current}-${currentField}` : undefined,
    'aria-activedescendant': open && field === currentField && active >= 0 ? `${id.current}-${currentField}-${active}` : undefined,
    ref: node => { inputRefs.current[currentField] = node; },
    onFocus: () => { setField(currentField); setDismissed(false); }, onKeyDown: keyboard,
    autoCorrect: 'off', spellCheck: false, autoComplete: 'off'
  });
  return <div className="customer-autocomplete" ref={root} onBlur={event => {
    if (!event.currentTarget.contains(event.relatedTarget)) { setField(null); setDismissed(true); }
  }}>
    <h4>Kunde</h4>
    <div className="customer-field-anchor">
      <label className="appointment-field"><span>Kundenname</span><input {...inputProps('name')} type="text" value={data.clientName || ''} onChange={event => change('clientName', event.target.value)} placeholder="Name eingeben · Bestandskunden finden" required /></label>
      {suggestions('name')}
    </div>
    <div className="customer-field-anchor">
      <label className="appointment-field"><span>Telefonnummer · optional</span><input {...inputProps('phone')} type="tel" inputMode="tel" className="appointment-phone-input" aria-label="Telefonnummer" value={data.phone || ''} onChange={event => change('phone', event.target.value)} placeholder="Telefonnummer" /></label>
      {suggestions('phone')}
    </div>
    <label className="appointment-field"><span>E-Mail · optional</span><input type="email" autoComplete="off" value={data.email || ''} onChange={event => change('email', event.target.value)} placeholder="E-Mail-Adresse" /></label>
    {selected && <div className="customer-recognized" role="status"><div className="customer-recognized-top"><strong>✓ Bestandskunde ausgewählt</strong><button type="button" aria-label="Kundenauswahl lösen" onClick={() => { setData(previous => ({ ...previous, autocompleteCustomer: null })); setDismissed(true); }}>×</button></div>
      {selected.lastAppointment && <div>Zuletzt: {appointmentLabel(selected.lastAppointment)}</div>}
      {selected.nextAppointment && <div className="customer-existing-booking">Bereits geplant: {appointmentLabel(selected.nextAppointment)}</div>}
    </div>}
  </div>;
}
